import { resolveSpeakerName } from './resolve-speaker-name';
import type { TranscriptSegmentData } from './parse-transcript';
import type { SpeakerLabel } from './types/types';

/** A ScrollRail anchor for one transcript turn. */
export interface TurnAnchor {
  /** Anchor id — matches the turn's `data-anchor-id`. */
  id: string;
  /** Resolved speaker display name (same resolution the turns use). */
  label: string;
  /** Index of the turn (the scroll target). */
  turnIndex: number;
}

/** Max chars of turn text kept in a label — the popover CSS ellipsis-clamps the
 *  visible row, this just keeps the aria-label (and string) from being huge. */
const MAX_TEXT = 80;

/** Capitalize the first letter (so a lowercase speaker like "bruno" reads
 *  "Bruno"; already-capitalized names like "Chris Tan" are unchanged). */
const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * One rail anchor per transcript turn, labelled `"<Speaker>: <text>"` — the
 * speaker resolves the same way the turns do (via `resolveSpeakerName`,
 * honouring label/attendee overrides) and the turn's text is appended (capped
 * at MAX_TEXT with an ellipsis; the popover CSS also visually clamps to one
 * line). When a turn has no text, the label is just the speaker name. Per-turn
 * (not per speaker-block) so the rail stays useful on single-speaker recordings.
 */
export function buildTurnAnchors(
  segments: TranscriptSegmentData[],
  speakerLabels: Record<string, SpeakerLabel>,
  attendeeNameByEmail: Record<string, string>,
): TurnAnchor[] {
  // The set of real attendee display names — used to decide whether a resolved
  // speaker is a name (→ first name only) vs an email-local-part fallback /
  // raw "Speaker N" (→ kept whole).
  const attendeeNames = new Set(Object.values(attendeeNameByEmail));

  return segments.map((seg, i) => {
    const resolved = resolveSpeakerName(seg, speakerLabels, attendeeNameByEmail);
    // For an actual attendee name, show first name only in the rail; otherwise
    // keep the value as-is (email fallback, "Speaker N", edited label).
    const named = attendeeNames.has(resolved);
    const speaker = capitalize(named ? resolved.split(/\s+/)[0] : resolved);
    const text = (seg.text ?? '').trim();
    const clamped = text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT)}…` : text;
    return {
      id: `turn-${i}`,
      label: clamped ? `${speaker}: ${clamped}` : speaker,
      turnIndex: i,
    };
  });
}
